import { useEffect, useRef, useState } from 'react';

const DEFAULT_FLOATING_TOP_THRESHOLD = 145;
const DEFAULT_SIDE_PADDING = 12;
const DEFAULT_MAX_BAR_WIDTH = 1400;

const getScrollParent = (el) => {
  if (!el) return null;
  let parent = el.parentElement;
  while (parent) {
    const { overflowY } = getComputedStyle(parent);
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
};

export const useSelectionActionBarFloating = ({
  floatingTopThreshold = DEFAULT_FLOATING_TOP_THRESHOLD,
  sidePadding = DEFAULT_SIDE_PADDING,
  maxBarWidth = DEFAULT_MAX_BAR_WIDTH,
} = {}) => {
  const anchorRef = useRef(null);
  const animationFrameRef = useRef(null);
  const [isFloating, setIsFloating] = useState(false);
  const [floatingStyle, setFloatingStyle] = useState(null);

  useEffect(() => {
    const updateFloating = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;

      const { top, left, width } = anchor.getBoundingClientRect();
      const shouldFloat = top < floatingTopThreshold;

      if (shouldFloat) {
        const viewportWidth = document.documentElement.clientWidth;
        const isIpadUserAgent = /iPad/i.test(window.navigator.userAgent)
          || (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
        const isIpadViewport = viewportWidth >= 768 && viewportWidth <= 1180;
        const isIpadLike = isIpadUserAgent || isIpadViewport;
        const leftOffset = isIpadLike ? 80 : sidePadding;
        const rightPadding = isIpadLike ? 0 : sidePadding;
        const maxWidthByViewport = viewportWidth - leftOffset - rightPadding;
        const computedWidth = Math.min(width, maxBarWidth, maxWidthByViewport);
        const centeredLeft = left + (width - computedWidth) / 2;
        const computedLeft = isIpadLike
          ? leftOffset
          : Math.max(
            sidePadding,
            Math.min(centeredLeft, viewportWidth - computedWidth - rightPadding)
          );
        const nextStyle = {
          width: `${computedWidth}px`,
          left: `${computedLeft}px`,
          transform: 'none',
        };
        setFloatingStyle((prev) => {
          if (
            prev?.width === nextStyle.width
            && prev?.left === nextStyle.left
            && prev?.transform === nextStyle.transform
          ) {
            return prev;
          }
          return nextStyle;
        });
      } else {
        setFloatingStyle((prev) => (prev === null ? prev : null));
      }

      setIsFloating((prev) => (prev === shouldFloat ? prev : shouldFloat));
    };

    const handleScroll = () => {
      if (animationFrameRef.current != null) return;
      animationFrameRef.current = window.requestAnimationFrame(() => {
        animationFrameRef.current = null;
        updateFloating();
      });
    };

    updateFloating();

    const scrollParent = getScrollParent(anchorRef.current);
    const scrollTarget = scrollParent || window;
    scrollTarget.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);

    return () => {
      scrollTarget.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (animationFrameRef.current != null) {
        window.cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [floatingTopThreshold, maxBarWidth, sidePadding]);

  return {
    anchorRef,
    isFloating,
    floatingStyle,
  };
};

export default useSelectionActionBarFloating;
